import { useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, Loader2, X } from "lucide-react";
import { useApi, errMsg } from "@/lib/api";
import { fmtDT } from "@/lib/brand";
import { AuthImage } from "@/components/site/AuthImage";

const inputCls = "mt-1 w-full rounded-md border border-white/10 bg-[#0A0A0A] px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#FF5A00]";

function PhaseEditor({ b, phase, onSaved }) {
  const api = useApi();
  const cur = b.inspections?.[phase] || {};
  const [f, setF] = useState({ odometer_km: cur.odometer_km ?? "", fuel_pct: cur.fuel_pct ?? "", notes: cur.notes || "", photos: cur.photos || [] });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(0);
  const fileRef = useRef(null);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const upload = async (files) => {
    setUploading(files.length);
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file);
      try {
        const r = await api.post(`/admin/bookings/${b.id}/inspection-photos`, fd);
        setF((p) => ({ ...p, photos: [...p.photos, r.data.id] }));
      } catch (e) { toast.error(`${file.name}: ${errMsg(e)}`); }
      setUploading((n) => n - 1);
    }
  };
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/bookings/${b.id}/inspection/${phase}`, {
        odometer_km: f.odometer_km === "" ? null : Number(f.odometer_km), fuel_pct: f.fuel_pct === "" ? null : Number(f.fuel_pct), notes: f.notes, photos: f.photos,
      });
      toast.success(`${phase === "pickup" ? "Pickup" : "Return"} inspection saved`);
      onSaved();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  return (
    <div className="space-y-3 rounded-md border border-white/10 p-4" data-testid={`inspection-${phase}`}>
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold">{phase === "pickup" ? "Pickup inspection" : "Return inspection"}</p>
        {cur.at && <p className="text-xs text-neutral-500">Saved {fmtDT(cur.at)} · {cur.by}</p>}
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="text-xs text-neutral-400">Odometer (km)<input type="number" min="0" value={f.odometer_km} onChange={(e) => set("odometer_km", e.target.value)} data-testid={`inspection-${phase}-odometer`} className={inputCls} /></label>
        <label className="text-xs text-neutral-400">Fuel / charge (%)<input type="number" min="0" max="100" value={f.fuel_pct} onChange={(e) => set("fuel_pct", e.target.value)} data-testid={`inspection-${phase}-fuel`} className={inputCls} /></label>
      </div>
      <label className="block text-xs text-neutral-400">Damage &amp; notes<textarea rows={2} value={f.notes} onChange={(e) => set("notes", e.target.value)} data-testid={`inspection-${phase}-notes`} className={inputCls} placeholder="e.g. small scratch on rear bumper" /></label>
      <div className="flex flex-wrap gap-2">
        {f.photos.map((pid) => (
          <div key={pid} className="relative h-16 w-24 overflow-hidden rounded bg-white/5">
            <AuthImage src={`/bookings/${b.id}/inspection-photos/${pid}`} className="h-16 w-24 object-cover" />
            <button type="button" onClick={() => set("photos", f.photos.filter((x) => x !== pid))} className="absolute right-1 top-1 rounded bg-black/70 p-0.5 text-white" aria-label="Remove photo"><X className="h-3 w-3" /></button>
          </div>
        ))}
        <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading > 0} data-testid={`inspection-${phase}-add-photos`}
          className="flex h-16 w-24 flex-col items-center justify-center gap-1 rounded border border-dashed border-white/20 text-xs text-neutral-400 hover:border-[#FF5A00]">
          {uploading > 0 ? <><Loader2 className="h-4 w-4 animate-spin" />{uploading} left</> : <><Camera className="h-4 w-4" />Add photos</>}
        </button>
        <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" multiple capture="environment" hidden data-testid={`inspection-${phase}-file`}
          onChange={(e) => { upload([...e.target.files]); e.target.value = ""; }} />
      </div>
      <button onClick={save} disabled={busy || uploading > 0} data-testid={`inspection-${phase}-save`} className="rounded-md bg-[#FF5A00] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">{busy ? "Saving…" : "Save inspection"}</button>
    </div>
  );
}

export function InspectionEditor({ b, onSaved }) {
  if (["PENDING_PAYMENT", "CANCELLED"].includes(b.status)) return null;
  return (
    <div className="space-y-3">
      <PhaseEditor key={`${b.id}-pickup`} b={b} phase="pickup" onSaved={onSaved} />
      {["ACTIVE", "COMPLETED"].includes(b.status) && <PhaseEditor key={`${b.id}-return`} b={b} phase="return" onSaved={onSaved} />}
    </div>
  );
}
